import { useEffect, useRef, useState } from "react";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { SidebarProvider } from "@/components/ui/sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Copy, Upload, Handshake, MousePointerClick, TrendingUp, Wallet } from "lucide-react";
import { toast } from "sonner";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const COULEURS = ["#c8a96e", "#6c8ebf", "#82b366", "#b85450", "#9673a6"];

const LIBELLE_PROGRAMME: Record<string, string> = { a_candidater: "À candidater", en_attente: "En attente de validation", accepte: "Accepté", refuse: "Refusé" };
const LIBELLE_RECO: Record<string, string> = { prepare: "Préparée", lien_envoye: "Lien envoyé", inscrit: "Inscrit", active: "Activé", refuse: "Refusé" };
const LIBELLE_COM: Record<string, string> = { en_attente: "En attente", disponible: "Disponible", en_versement: "En versement", payee: "Payée" };
const LIBELLE_TYPE: Record<string, string> = { inscription: "Inscription", bonus_activation: "Bonus d'activation", autre: "Autre" };

interface Programme { id: string; nom: string; plateforme: string; statut: string; lien_trace: string; commissions: { type: string; montant_eur: number | null }[]; conditions: string[]; entite: string; clics: number; note: string; indicateurs?: { taux_conversion_pct?: number | null; taux_validation_pct?: number | null; epc_eur?: number | null; delai_paiement_jours?: number | null; source?: string } }
interface Reco { id: string; programme_nom: string; client_nom: string; client_email: string; statut: string; date_lien?: string }
interface Commission { id: string; programme_nom: string; type: string; montant_eur: number; statut: string; date_conversion: string; entite: string }
interface Stats { clics: number; clics_mesures: boolean; recommandations: number; conversions: number; taux_conversion: number | null; commissions_par_statut: Record<string, number>; ca_mois: number; ca_cumule: number; encaisse_par_entite: Record<string, number> }

const eur = (n: number) => `${n.toLocaleString("fr-FR", { maximumFractionDigits: 2 })} €`;

export default function Affiliation() {
  const [programmes, setProgrammes] = useState<Programme[]>([]);
  const [recos, setRecos] = useState<Reco[]>([]);
  const [commissions, setCommissions] = useState<Commission[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [mensuel, setMensuel] = useState<any>({ programmes: [], mois: [] });
  const [importPour, setImportPour] = useState("");
  const fichier = useRef<HTMLInputElement>(null);
  const [liens, setLiens] = useState<Record<string, string>>({});

  const get = async (chemin: string) => {
    const r = await fetch(`${BACKEND}/api/affiliation/${chemin}`, { credentials: "include" });
    return r.ok ? r.json() : null;
  };
  const charger = async () => {
    const [p, r, c, s, m] = await Promise.all([get("programmes"), get("recommandations"), get("commissions"), get("stats"), get("mensuel")]);
    if (p) setProgrammes(p.programmes);
    if (r) setRecos(r.recommandations);
    if (c) setCommissions(c.commissions);
    if (s) setStats(s);
    if (m) setMensuel(m);
  };
  useEffect(() => { charger(); }, []);

  const copier = async (p: Programme) => {
    if (!p.lien_trace) return toast.error("Pas encore de lien traqué : le programme doit d'abord être accepté");
    try { await navigator.clipboard.writeText(p.lien_trace); toast.success("Lien copié"); } catch { toast.error("Copie impossible sur cet appareil"); }
  };

  const importer = async (f: File) => {
    if (!importPour) return toast.error("Choisissez d'abord le programme concerné");
    const csv = await f.text();
    const r = await fetch(`${BACKEND}/api/affiliation/import-csv`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ programme_id: importPour, csv }) });
    const d = await r.json().catch(() => ({}));
    if (r.ok) { toast.success(`${d.ajoutees} ajoutée(s), ${d.doublons} déjà connue(s), ${d.ignorees} ignorée(s)`); charger(); }
    else toast.error(typeof d.detail === "string" ? d.detail : "Import impossible");
    if (fichier.current) fichier.current.value = "";
  };

  const enregistrerLien = async (p: Programme) => {
    const lien = (liens[p.id] || "").trim();
    if (!lien.startsWith("https://")) return toast.error("Collez l'adresse complète du lien, qui commence par https://");
    const r = await fetch(`${BACKEND}/api/affiliation/programmes/${p.id}`, { method: "PATCH", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ lien_trace: lien }) });
    if (r.ok) { toast.success("Lien enregistré, programme accepté"); setLiens((x) => ({ ...x, [p.id]: "" })); charger(); }
    else toast.error("Enregistrement impossible");
  };

  const majCommission = async (c: Commission, statut: string) => {
    const r = await fetch(`${BACKEND}/api/affiliation/commissions/${c.id}`, { method: "PATCH", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ statut }) });
    if (r.ok) { toast.success("Statut mis à jour"); charger(); }
  };

  const st = stats?.commissions_par_statut || {};
  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <DashboardSidebar />
        <main className="flex-1 p-6 md:p-10 overflow-y-auto">
          <div className="max-w-6xl mx-auto space-y-8">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Affiliation</h1>
              <p className="text-muted-foreground mt-1">Commissions touchées en recommandant des partenaires à vos clients. Montants HT. Les apports par e-mail (Kandbaz) sont dans l'onglet Partenaires.</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card><CardContent className="pt-5 flex items-center gap-3"><MousePointerClick className="h-6 w-6 text-amber-400" /><div>
                <div className="text-2xl font-bold">{stats?.clics_mesures ? stats.clics : "—"}</div>
                <div className="text-xs text-muted-foreground">Clics {stats?.clics_mesures ? "" : "(pas encore mesurés)"}</div></div></CardContent></Card>
              <Card><CardContent className="pt-5 flex items-center gap-3"><Handshake className="h-6 w-6 text-amber-400" /><div>
                <div className="text-2xl font-bold">{stats?.conversions ?? 0}</div>
                <div className="text-xs text-muted-foreground">Conversions · taux {stats?.taux_conversion != null ? `${stats.taux_conversion} %` : "—"}</div></div></CardContent></Card>
              <Card><CardContent className="pt-5 flex items-center gap-3"><TrendingUp className="h-6 w-6 text-amber-400" /><div>
                <div className="text-2xl font-bold">{eur(stats?.ca_mois ?? 0)}</div>
                <div className="text-xs text-muted-foreground">Ce mois · {eur(stats?.ca_cumule ?? 0)} cumulé</div></div></CardContent></Card>
              <Card><CardContent className="pt-5 flex items-center gap-3"><Wallet className="h-6 w-6 text-amber-400" /><div>
                <div className="text-sm leading-5">En attente <b>{eur(st.en_attente || 0)}</b></div>
                <div className="text-sm leading-5">Disponible <b>{eur(st.disponible || 0)}</b></div>
                <div className="text-sm leading-5">Payée <b>{eur(st.payee || 0)}</b></div></div></CardContent></Card>
            </div>
            {stats && Object.keys(stats.encaisse_par_entite).length > 0 && (
              <p className="text-xs text-muted-foreground">Encaissé par entité : {Object.entries(stats.encaisse_par_entite).map(([n, v]) => `${n} ${eur(v)}`).join(" · ")}</p>
            )}

            <Card>
              <CardHeader><CardTitle>Programmes</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {programmes.length === 0 && <p className="text-sm text-muted-foreground">Aucun programme pour l'instant.</p>}
                {programmes.map((p) => (
                  <div key={p.id} className="border rounded-lg p-4 space-y-2">
                    <div className="flex flex-wrap items-center gap-2 justify-between">
                      <div className="flex items-center gap-2"><span className="font-semibold">{p.nom}</span><Badge variant="outline">{p.plateforme}</Badge><Badge>{LIBELLE_PROGRAMME[p.statut] || p.statut}</Badge></div>
                      <Button size="sm" variant="outline" className="gap-1.5" disabled={!p.lien_trace} title={p.lien_trace ? "" : "Disponible dès que le programme est accepté et que le lien est collé"} onClick={() => copier(p)}><Copy className="h-3.5 w-3.5" />Copier le lien</Button>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {p.commissions.map((c, i) => <span key={i} className="mr-4">{LIBELLE_TYPE[c.type] || c.type} : {c.montant_eur != null ? eur(c.montant_eur) : "montant non communiqué"}</span>)}
                      <span>Encaissé par {p.entite}</span>
                    </div>
                    {p.indicateurs && (p.indicateurs.taux_conversion_pct != null || p.indicateurs.epc_eur != null) && (
                      <p className="text-xs text-muted-foreground">
                        Relevé dans la plateforme : conversion {p.indicateurs.taux_conversion_pct ?? "—"} % · validation {p.indicateurs.taux_validation_pct ?? "—"} % · gain par clic {p.indicateurs.epc_eur ?? "—"} € · paiement sous ~{p.indicateurs.delai_paiement_jours ?? "—"} jours
                      </p>
                    )}
                    {!p.lien_trace && (
                      <div className="flex gap-2 pt-1">
                        <input className="border rounded-md bg-background px-2 h-9 text-sm flex-1" placeholder="Coller ici le lien traqué une fois le programme accepté (https://…)" value={liens[p.id] || ""} onChange={(e) => setLiens((x) => ({ ...x, [p.id]: e.target.value }))} />
                        <Button size="sm" onClick={() => enregistrerLien(p)}>Enregistrer le lien</Button>
                      </div>
                    )}
                    {p.conditions.length > 0 && <ul className="text-xs text-muted-foreground list-disc pl-5">{p.conditions.map((c, i) => <li key={i}>{c}</li>)}</ul>}
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>Recommandations par client</CardTitle></CardHeader>
              <CardContent>
                {recos.length === 0 ? <p className="text-sm text-muted-foreground">Aucune recommandation. Depuis une fiche client du CRM, utilisez « Recommander un partenaire ».</p> : (
                  <table className="w-full text-sm"><thead><tr className="text-left text-muted-foreground"><th className="py-1">Client</th><th>Programme</th><th>Statut</th></tr></thead>
                    <tbody>{recos.map((r) => (<tr key={r.id} className="border-t"><td className="py-2">{r.client_nom || r.client_email}</td><td>{r.programme_nom}</td><td><Badge variant="outline">{LIBELLE_RECO[r.statut] || r.statut}</Badge></td></tr>))}</tbody></table>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>Commissions</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap items-center gap-2">
                  <select className="border rounded-md bg-background px-2 h-9 text-sm" value={importPour} onChange={(e) => setImportPour(e.target.value)}>
                    <option value="">Importer un export pour…</option>{programmes.map((p) => <option key={p.id} value={p.id}>{p.nom}</option>)}
                  </select>
                  <input ref={fichier} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => e.target.files?.[0] && importer(e.target.files[0])} />
                  <Button size="sm" variant="outline" className="gap-1.5" onClick={() => fichier.current?.click()}><Upload className="h-3.5 w-3.5" />Importer un export CSV</Button>
                </div>
                {commissions.length === 0 ? <p className="text-sm text-muted-foreground">Aucune commission enregistrée.</p> : (
                  <table className="w-full text-sm"><thead><tr className="text-left text-muted-foreground"><th className="py-1">Date</th><th>Programme</th><th>Type</th><th>Montant HT</th><th>Entité</th><th>Statut</th></tr></thead>
                    <tbody>{commissions.map((c) => (
                      <tr key={c.id} className="border-t"><td className="py-2">{c.date_conversion}</td><td>{c.programme_nom}</td><td>{LIBELLE_TYPE[c.type] || c.type}</td><td>{eur(c.montant_eur)}</td><td>{c.entite}</td>
                        <td><select className="border rounded bg-background text-xs h-7 px-1" value={c.statut} onChange={(e) => majCommission(c, e.target.value)}>{Object.entries(LIBELLE_COM).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></td></tr>))}</tbody></table>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>Commissions par mois et par programme</CardTitle></CardHeader>
              <CardContent style={{ height: 280 }}>
                {mensuel.mois.length === 0 ? <p className="text-sm text-muted-foreground">Le graphique apparaîtra avec la première commission.</p> : (
                  <ResponsiveContainer width="100%" height="100%"><BarChart data={mensuel.mois}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="mois" /><YAxis /><Tooltip /><Legend />
                    {mensuel.programmes.map((n: string, i: number) => <Bar key={n} dataKey={n} stackId="a" fill={COULEURS[i % COULEURS.length]} />)}</BarChart></ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}
